import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './state/AuthContext';
import { RootRedirect } from './routes/RootRedirect';
import { OnboardingLayout } from './routes/OnboardingLayout';
import { AdminLayout } from './routes/AdminLayout';
import { CountryConfirm } from './pages/CountryConfirm';
import { InitialNotice } from './pages/InitialNotice';
import { ApplicationQuestions } from './pages/ApplicationQuestions';
import { SalesResources } from './pages/SalesResources';
import { FirstLeadTraining } from './pages/FirstLeadTraining';
import { JobDetail } from './pages/JobDetail';
import { KadieWidget } from './components/kadie/KadieWidget';

/**
 * A page kept out of the first download (it is loaded the first time it is opened, and fetched
 * quietly in the background once the site has started, so opening it later is still instant).
 */
const backgroundLoads: (() => Promise<unknown>)[] = [];
function lazyPage<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K, fallback: ReactNode) {
  backgroundLoads.push(load);
  const Page = lazy(() => load().then((module) => ({ default: module[name] }))) as unknown as ComponentType;
  return function LazyPage() {
    return (
      <Suspense fallback={fallback}>
        <Page />
      </Suspense>
    );
  };
}
function usePreloadPages() {
  useEffect(() => {
    const start = () => backgroundLoads.forEach((load) => void load().catch(() => undefined));
    const idle = (window as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (idle) idle(start);
    else window.setTimeout(start, 1500);
  }, []);
}

const loading = <div className="loading-state">Loading…</div>;
const StepAgreement = lazyPage(() => import('./pages/StepAgreement'), 'StepAgreement', loading);
const StepTaxForm = lazyPage(() => import('./pages/StepTaxForm'), 'StepTaxForm', loading);
const AdminDashboard = lazyPage(() => import('./pages/admin/AdminDashboard'), 'AdminDashboard', loading);
const AdminUserDetail = lazyPage(() => import('./pages/admin/AdminUserDetail'), 'AdminUserDetail', loading);
const AdminApplicationPreview = lazyPage(() => import('./pages/admin/AdminApplicationPreview'), 'AdminApplicationPreview', loading);
const AdminPositions = lazyPage(() => import('./pages/admin/AdminPositions'), 'AdminPositions', loading);

export default function App() {
  usePreloadPages();
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/jobs/:positionId" element={<JobDetail />} />
        <Route path="/onboarding" element={<OnboardingLayout />}>
          <Route path="country" element={<CountryConfirm />} />
          <Route path="notice" element={<InitialNotice />} />
          <Route path="application" element={<ApplicationQuestions />} />
          <Route path="resources" element={<SalesResources />} />
          <Route path="agreement" element={<StepAgreement />} />
          <Route path="tax-form" element={<StepTaxForm />} />
          <Route path="training" element={<FirstLeadTraining />} />
          {/* These all used to be dedicated pages with no content of their own beyond a status
              message - that status now shows inline on the page the applicant actually
              submitted from (or, for "final", the training page itself once approved), so
              these routes just redirect there (old bookmarks/emails still land somewhere real
              instead of a blank/broken page). */}
          <Route path="final" element={<Navigate to="/onboarding/training" replace />} />
          <Route path="application-review" element={<Navigate to="/onboarding/application" replace />} />
          <Route path="training-review" element={<Navigate to="/onboarding/training" replace />} />
          {['welcome', 'leads', 'prepare', 'call', 'follow-up', 'complete'].map((path) => <Route key={path} path={path} element={<Navigate to="/onboarding/training" replace />} />)}
        </Route>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="positions" element={<AdminPositions />} />
          {/* Admin-only, read-only preview of the live applicant application form (and its
              passing-template button). Admins are redirected out of /onboarding, and this
              preview saves and submits nothing, so it can never create applicant state. */}
          <Route path="application-preview" element={<AdminApplicationPreview />} />
          <Route path="application-preview/:jobType" element={<AdminApplicationPreview />} />
        </Route>
      </Routes>
      <KadieWidget />
    </AuthProvider>
  );
}
